import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faCloudArrowUp, faFile, faFileExcel, faFilePdf, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { UserUploadFile } from '../../../models/userUploadFile';
import { FileUploadService } from '../../../services/FileUpload.service';
import { FarmFileViewerDialogComponent } from '../farm-file-viewer-dialog/farm-file-viewer-dialog.component';
import { FarmFileKind, getFileKind } from './farm-file-utils';

type FarmFilter = 'all' | FarmFileKind;

@Component({
  selector: 'kw-farm-files',
  templateUrl: './farm-files.component.html',
  styleUrls: ['./farm-files.component.scss']
})
export class FarmFilesComponent implements OnChanges {
  @Input() farmId: number;
  @Input() canEdit = false;

  private destroyRef = inject(DestroyRef);

  public files: UserUploadFile[] = [];
  public isLoading = false;
  public isUploading = false;
  public isDragOver = false;
  public filter: FarmFilter = 'all';
  public filters: { key: FarmFilter, label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'image', label: 'Images' },
    { key: 'pdf', label: 'PDFs' },
    { key: 'excel', label: 'Spreadsheets' },
    { key: 'other', label: 'Other' },
  ];

  public faUpload = faCloudArrowUp;
  public faTrash = faTrash;
  public faFile = faFile;
  public faFilePdf = faFilePdf;
  public faFileExcel = faFileExcel;

  constructor(
    private fileService: FileUploadService,
    private dialog: MatDialog,
    private toastr: NbToastrService,
    private errorService: ErrorService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.farmId && this.farmId) {
      this.loadFiles();
    }
  }

  get filteredFiles(): UserUploadFile[] {
    return this.filter === 'all' ? this.files : this.files.filter(f => this.kind(f) === this.filter);
  }

  count(key: FarmFilter): number {
    return key === 'all' ? this.files.length : this.files.filter(f => this.kind(f) === key).length;
  }

  kind(file: UserUploadFile): FarmFileKind {
    return getFileKind(file.fileName);
  }

  loadFiles() {
    this.isLoading = true;
    this.fileService.getFarmFiles(this.farmId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (files) => {
        this.files = files || [];
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorService.handleError(err);
      }
    });
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.upload(Array.from(input.files || []));
    input.value = '';
  }

  onDragOver(event: DragEvent) {
    if (!this.canEdit) return;
    event.preventDefault();
    this.isDragOver = true;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    if (!this.canEdit) return;
    this.upload(Array.from(event.dataTransfer?.files || []));
  }

  private upload(selected: File[]) {
    if (!selected.length || this.isUploading) return;
    this.isUploading = true;
    this.fileService.postFarmFileUploads(selected, this.farmId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isUploading = false;
        this.toastr.success(`${selected.length} file${selected.length === 1 ? '' : 's'} uploaded.`, 'Uploaded');
        this.loadFiles();
      },
      error: (err) => {
        this.isUploading = false;
        this.errorService.handleError(err);
      }
    });
  }

  open(file: UserUploadFile) {
    const list = this.filteredFiles;
    this.dialog.open(FarmFileViewerDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      data: { files: list, index: list.indexOf(file) }
    });
  }

  confirmDelete(file: UserUploadFile, event: Event) {
    event.stopPropagation();
    const data: ConfirmationData = {
      title: 'Delete File',
      confirmMessage: `Delete "${file.fileName}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      displayComment: false,
      isWarning: false,
    };
    this.dialog.open(ConfirmationDialogComponent, { width: '400px', data })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
        if (result !== true) return;
        // Remove optimistically; restore the row if the delete fails.
        const index = this.files.indexOf(file);
        this.files = this.files.filter(f => f !== file);
        this.fileService.deleteFileUploads(file.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          error: (err) => {
            this.files = [...this.files.slice(0, index), file, ...this.files.slice(index)];
            this.changeDetector.markForCheck();
            this.errorService.handleError(err);
          }
        });
      });
  }
}
